import React, { useEffect, useState } from "react"
import { Container, Card, CardBody, Row, Col, Button, Input, Table, Nav, NavItem, NavLink } from "reactstrap"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import ReactApexChart from "react-apexcharts"
import Breadcrumbs from "../../components/Common/Breadcrumb"
import { getCampDashboard, getCampGroups, getCampCostAnalysis, getCampRentTransactions } from "../../services/campManagement"
import { toastError } from "../../helpers/msg_helper"
import useCollapsedSidebar from "../../hooks/useCollapsedSidebar"

// roles allowed to see the finance reports section
const REPORT_ROLES = ["Superadmin", "Accounts Manager", "developer"]
const money = v => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
const fmtDate = s => (s ? String(s).slice(0, 10).split("-").reverse().join("-") : "—")

// Rent Analysis register = ONE row per cheque. Each rent voucher's cheques each become a line
// item carrying that cheque's own amount + status; any part of the Net Rent not yet covered by a
// cheque becomes a single "Pending" line (a voucher with no cheque at all = one Pending line for
// its full Net Rent). Summed amounts therefore still equal total Net Rent. `first` marks the first
// line of each voucher so the shared columns (name / location / period) print once per group.
const buildChequeItems = rows =>
  (rows || []).flatMap(r => {
    const chqs = r.cheques || []
    let covered = 0
    const items = chqs.map(c => {
      covered += Number(c.amount) || 0
      return { r, cheque_no: c.no, cheque_date: c.date, amount: Number(c.amount) || 0, status: c.cleared ? "Cleared" : "Issued" }
    })
    const shortfall = Math.round(((Number(r.net_rent) || 0) - covered) * 100) / 100
    if (chqs.length === 0 || shortfall > 0.01) {
      items.push({ r, cheque_no: null, cheque_date: null, amount: shortfall > 0 ? shortfall : 0, status: "Pending" })
    }
    items.forEach((it, idx) => (it.first = idx === 0))
    return items
  })
const statusColor = s => (s === "Cleared" ? "#16a34a" : s === "Issued" ? "#d97706" : "#64748b")

const Dot = ({ c }) => <span style={{ display: "inline-block", width: 10, height: 10, borderRadius: 2, background: c, marginRight: 4, verticalAlign: "middle" }} />

const Kpi = ({ label, value, color }) => (
  <Col>
    <Card className="text-center h-100">
      <CardBody className="px-2">
        <div className="text-muted text-uppercase" style={{ fontSize: 10.5, letterSpacing: ".02em", whiteSpace: "nowrap" }}>{label}</div>
        <h3 className="mt-2 mb-0" style={{ color }}>{value ?? "—"}</h3>
      </CardBody>
    </Card>
  </Col>
)

const CampDashboard = () => {
  useCollapsedSidebar()
  const navigate = useNavigate()
  document.title = "Camp Dashboard"
  const [d, setD] = useState(null)
  const [g, setG] = useState(null)

  // finance reports (Superadmin / Accounts Manager only)
  const loggedUser = useSelector(s => s.Login?.loggedUser)
  const roleNames = (loggedUser?.data?.roles || loggedUser?.roles || []).map(r => r?.name).filter(Boolean)
  const canSeeReports = roleNames.some(n => REPORT_ROLES.includes(n))
  const thisYear = new Date().getFullYear()
  const [year, setYear] = useState(thisYear)
  const [reportTab, setReportTab] = useState("rent")
  const [cost, setCost] = useState(null)
  const [rent, setRent] = useState(null)

  useEffect(() => {
    ;(async () => {
      try {
        const [r, gr] = await Promise.all([getCampDashboard(), getCampGroups()])
        setD(r?.data); setG(gr?.data)
      } catch (e) { toastError(e?.message || "Failed to load dashboard") }
    })()
  }, [])

  useEffect(() => {
    if (!canSeeReports) return
    ;(async () => {
      try {
        const [c, rr] = await Promise.all([getCampCostAnalysis(year), getCampRentTransactions(year)])
        setCost(c?.data); setRent(rr?.data)
      } catch (e) { /* reports are best-effort */ }
    })()
  }, [canSeeReports, year])

  // ---- Export (Rent Analysis) : dependency-free XLS (HTML table) + PDF (print window) ----
  const buildRentTables = () => {
    const rows = rent?.rows || []
    const abs = rent?.abstract || []
    const esc = v => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    const period = r => (r.period_from
      ? `${fmtDate(r.period_from)}${r.period_to ? " → " + fmtDate(r.period_to) : ""} (${r.period === "Yearly"
        ? "Yearly " + Math.max(1, Math.round((r.months || 12) / 12)) + "yr"
        : "Monthly " + (r.months || 1) + "mo"})`
      : "—")
    // Net Rent = camp rent + commission + security cheque (VAT excluded), with the components shown.
    // Voucher no rides under the name, and the cheque no over its date, to keep the table narrow.
    // Per-cheque register: one row per cheque; name/breakdown/location/period print once per voucher.
    // Cheque no + date + status share one cell.
    const invHead = ["Name", "Cheque No / Date", "Amount", "Camp Rent", "Commission", "Security", "Net Rent", "Location", "Rooms", "Period"]
    const chequeItems = buildChequeItems(rows)
    const vsum = k => rows.reduce((a, r) => a + (Number(r[k]) || 0), 0)
    const chqCell = it => (it.cheque_no ? `${it.cheque_no} ${fmtDate(it.cheque_date)}\n${it.status}` : it.status)
    const invBody = chequeItems.map(it => [
      it.first ? `${it.r.supplier || "—"}\n${[it.r.ref, fmtDate(it.r.date)].filter(Boolean).join(" · ")}` : "",
      chqCell(it),
      money(it.amount),
      it.first ? money(it.r.camp_rent) : "", it.first ? money(it.r.commission) : "", it.first ? money(it.r.security_cheque) : "", it.first ? money(it.r.net_rent) : "",
      it.first ? (it.r.location || "") : "", it.first ? (it.r.room_count || "—") : "", it.first ? period(it.r) : "",
    ])
    const invTotal = ["Total", "", money(chequeItems.reduce((a, it) => a + (Number(it.amount) || 0), 0)), money(vsum("camp_rent")), money(vsum("commission")), money(vsum("security_cheque")), money(rent?.total), "", "", ""]
    // Duration rides under the camp name, and rooms/capacity share one cell, to keep these narrow
    const t = rent?.abstract_total || {}
    const dash = v => (v != null ? money(v) : "—")
    const pair = (a, b) => `${a || "—"} / ${b || "—"}`
    const absPeriod = a => [a.duration, a.period_from ? `${fmtDate(a.period_from)}${a.period_to ? " → " + fmtDate(a.period_to) : ""}` : ""].filter(Boolean).join(" · ")
    const absHead = ["Camp", "Camp Rent", "Commission", "Security", "Net Rent", "Cleared", "Issued", "Not Issued", "Rooms / Capacity", "Rent / Emp", "+ Commission / Emp", "+ Comm + VAT / Emp"]
    const absBody = abs.map(a => [`${a.camp}\n${absPeriod(a)}`, money(a.camp_rent), money(a.commission), money(a.security_cheque), money(a.net_rent), money(a.cleared), money(a.issued), a.need_to_give ? money(a.need_to_give) : "—", pair(a.room_count, a.capacity), dash(a.rent_per_employee), dash(a.rent_comm_per_employee), dash(a.rent_vat_per_employee)])
    const absTotal = ["Total", money(t.camp_rent), money(t.commission), money(t.security_cheque), money(t.net_rent), money(t.cleared), money(t.issued), money(t.need_to_give), pair(t.room_count, t.capacity), dash(t.rent_per_employee), dash(t.rent_comm_per_employee), dash(t.rent_vat_per_employee)]
    // 3rd report — actual (over-)capacity basis
    const actHead = ["Camp", "Camp Rent", "Commission", "Security", "Net Rent", "Cleared", "Issued", "Not Issued", "Rooms / Actual Capacity", "Rent / Emp", "+ Commission / Emp", "+ Comm + VAT / Emp"]
    const actBody = abs.map(a => [`${a.camp}\n${absPeriod(a)}`, money(a.camp_rent), money(a.commission), money(a.security_cheque), money(a.net_rent), money(a.cleared), money(a.issued), a.need_to_give ? money(a.need_to_give) : "—", pair(a.room_count, a.actual_capacity), dash(a.rent_per_actual), dash(a.rent_comm_per_actual), dash(a.rent_vat_per_actual)])
    const actTotal = ["Total", money(t.camp_rent), money(t.commission), money(t.security_cheque), money(t.net_rent), money(t.cleared), money(t.issued), money(t.need_to_give), pair(t.room_count, t.actual_capacity), dash(t.rent_per_actual), dash(t.rent_comm_per_actual), dash(t.rent_vat_per_actual)]
    const numCols = new Set([2, 3, 4, 5, 6, 8]) // inv register: Amount + breakdown + Rooms
    // a cell may carry extra lines after the first ("main\nsub\nsub…"), e.g. name → voucher no +
    // date, or one line per cheque with its own cheque date
    const cell = v => {
      const [main, ...rest] = String(v ?? "").split("\n")
      const subs = rest.map(s => s.trim()).filter(Boolean)
      return esc(main) + subs.map(s => `<br><span class="sub">${esc(s)}</span>`).join("")
    }
    const section = (title, head, body, total) => `
      <tr><td class="sec" colspan="${head.length}">${esc(title)}</td></tr>
      <tr>${head.map((h, i) => `<th class="${(head === invHead ? numCols.has(i) : i >= 1) ? "num" : ""}">${esc(h)}</th>`).join("")}</tr>
      ${body.map(r => `<tr>${r.map((c, i) => `<td class="${(head === invHead ? numCols.has(i) : i >= 1) ? "num" : ""}">${cell(c)}</td>`).join("")}</tr>`).join("")}
      <tr class="tot">${total.map((c, i) => `<td class="${(head === invHead ? numCols.has(i) : i >= 1) ? "num" : ""}">${cell(c)}</td>`).join("")}</tr>
      <tr><td colspan="${head.length}" class="gap"></td></tr>`
    // ---- Cost Analysis sections (the other tab) ----
    const cm = cost?.months || []
    const eh = cost?.expense_heads || []
    const rh = cost?.recovery_heads || []
    const hasInc = !!cost?.has_incentive
    const ct = cost?.totals || {}
    const perHr = v => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }))
    const num = v => (v == null ? "—" : Number(v).toLocaleString())

    const costHead = ["Month", ...eh, "Total Expenses", ...rh, ...(hasInc ? ["Incentive (−)"] : []), "Total Recovered", "Net Cost", "Invoiced Hours", "Cost / Hour", "Employee Count", "Per Head / Month"]
    const costBody = cm.map(m => [
      m.label, ...eh.map(h => money(m.expenses?.[h])), money(m.total_expenses),
      ...rh.map(h => money(m.recovered?.[h])), ...(hasInc ? [money(m.incentive)] : []),
      money(m.total_recovered), money(m.net_cost),
      m.invoiced_hours != null ? num(m.invoiced_hours) : "—",
      m.cost_per_hour != null ? money(m.cost_per_hour) : "—",
      m.employee_count != null ? num(m.employee_count) : "—",
      m.per_head != null ? money(m.per_head) : "—",
    ])
    const costTotal = ["Total", ...eh.map(h => money(ct.expenses?.[h])), money(ct.total_expenses),
      ...rh.map(h => money(ct.recovered?.[h])), ...(hasInc ? [money(ct.incentive)] : []),
      money(ct.total_recovered), money(ct.net_cost), "—", "—", "—", "—"]

    // 2nd cost report — Cost per Hour (amount + per-hour, per expense head)
    const cphHead = ["Month", "Working Hours", ...eh.flatMap(h => [`${h} Amount`, `${h} / Hr`]), "Total Expenses", "Cost / Hour"]
    const cphBody = cm.map(m => [
      m.label, m.working_hours != null ? num(m.working_hours) : "—",
      ...eh.flatMap(h => [money(m.expenses?.[h]), perHr(m.expense_cost_per_hour?.[h])]),
      money(m.total_expenses), perHr(m.total_cost_per_hour),
    ])
    const cphTotal = ["Total", ct.working_hours != null ? num(ct.working_hours) : "—",
      ...eh.flatMap(h => [money(ct.expenses?.[h]), "—"]), money(ct.total_expenses), "—"]

    const costSections = cm.length
      ? section(`Cost Analysis — ${year}`, costHead, costBody, costTotal) +
        section(`Cost per Hour — ${year}`, cphHead, cphBody, cphTotal)
      : ""

    return section(`Rent Details — ${year}`, invHead, invBody, invTotal) +
      (abs.length ? section(`Main Abstract — Occupied Capacity — ${year}`, absHead, absBody, absTotal) : "") +
      (abs.length ? section(`Main Abstract — Actual Capacity — ${year}`, actHead, actBody, actTotal) : "") +
      costSections
  }

  const rentReportHtml = forPrint => `<!doctype html><html><head><meta charset="utf-8"><title>Camp Cost Reports ${year}</title>
    <style>
      body{font-family:Arial,Helvetica,sans-serif;font-size:12px;color:#1e293b;margin:${forPrint ? "18px" : "0"}}
      h1{font-size:16px;margin:0 0 10px}
      table{border-collapse:collapse;width:100%}
      th,td{border:1px solid #cbd5e1;padding:6px 8px;text-align:left}
      th{background:#f1f5f9;font-weight:600}
      td.num,th.num{text-align:right}
      td.sec{background:#e0e7ff;font-weight:700;border:1px solid #cbd5e1}
      tr.tot td{font-weight:700;background:#f8fafc}
      td.gap{border:none;height:10px}
      .sub{color:#64748b;font-size:10px}
    </style></head><body>
    ${forPrint ? `<h1>Camp Cost Reports — ${year}</h1>` : ""}
    <table>${buildRentTables()}</table>
    ${forPrint ? "<script>window.onload=function(){window.print()}</script>" : ""}
  </body></html>`

  const exportRentXls = () => {
    const blob = new Blob(["﻿" + rentReportHtml(false)], { type: "application/vnd.ms-excel" })
    const a = document.createElement("a")
    a.href = URL.createObjectURL(blob)
    a.download = `Camp_Cost_Reports_${year}.xls`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    setTimeout(() => URL.revokeObjectURL(a.href), 1000)
  }

  const exportRentPdf = () => {
    const w = window.open("", "_blank")
    if (!w) { toastError("Allow pop-ups to export PDF"); return }
    w.document.write(rentReportHtml(true))
    w.document.close()
  }

  const byCamp = d?.by_camp || []
  const byGroup = g?.by_group || []

  // By Camp — horizontal stacked bar: Occupied + Vacant (= Capacity) per camp
  const campChart = {
    series: [
      { name: "Occupied", data: byCamp.map(c => Number(c.occupied) || 0) },
      { name: "Vacant", data: byCamp.map(c => Number(c.vacant) || 0) },
    ],
    options: {
      chart: {
        type: "bar", stacked: true, toolbar: { show: false },
        events: { dataPointSelection: (e, ctx, cfg) => { const c = byCamp[cfg.dataPointIndex]; if (c?.camp_id) navigate(`/camp-manager?camp=${c.camp_id}`) } },
      },
      plotOptions: { bar: { horizontal: true, barHeight: "62%" } },
      colors: ["#e74c3c", "#e69a16"],
      xaxis: { categories: byCamp.map(c => c.camp) },
      yaxis: { labels: { maxWidth: 220, style: { fontSize: "12px" } } },
      legend: { show: false },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: v => `${v} beds` } },
      grid: { borderColor: "#f1f1f1" },
    },
  }

  // Workforce by Camp Group — grouped bars: Employees vs Bed Capacity per group
  const groupChart = {
    series: [
      { name: "Employees", data: byGroup.map(x => Number(x.employees) || 0) },
      { name: "Bed Capacity", data: byGroup.map(x => Number(x.capacity) || 0) },
    ],
    options: {
      chart: {
        type: "bar", toolbar: { show: false },
        events: { dataPointSelection: (e, ctx, cfg) => { const x = byGroup[cfg.dataPointIndex]; if (x) navigate(`/hrms-workforce?camp=${encodeURIComponent(x.group)}`) } },
      },
      plotOptions: { bar: { horizontal: false, columnWidth: "55%", borderRadius: 3 } },
      colors: ["#2f57c4", "#1a9d5f"],
      xaxis: { categories: byGroup.map(x => x.group) },
      legend: { show: false },
      dataLabels: { enabled: false },
      grid: { borderColor: "#f1f1f1" },
    },
  }

  return (
    <div className="page-content report-page">
      <Container fluid>
        <div className="d-flex justify-content-between align-items-center">
          <Breadcrumbs title="Camp Management" breadcrumbObject={[
            { title: "Camp Management", link: "/camp-manager" },
            { title: "Dashboard", link: "#" },
          ]} />
          <Button color="light" size="sm" className="mb-3" onClick={() => navigate("/camp-manager")}>Camp Manager</Button>
        </div>

        <Row className="g-2 mb-2">
          <Kpi label="Hired Rooms" value={d?.hired_rooms} color="#1f2937" />
          <Kpi label="Capacity" value={d?.capacity} color="#1a9d5f" />
          <Kpi label="Occupied" value={d?.occupied} color="#e74c3c" />
          <Kpi label="Vacant" value={d?.vacant} color="#e69a16" />
          <Kpi label="Office / Staff Room" value={d?.office_staff_rooms} color="#2f57c4" />
        </Row>

        <Row className="g-3">
          {/* By Camp — occupancy per camp (click a bar → that camp) */}
          <Col lg={6}>
            <Card className="h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap">
                  <h6 className="fw-semibold mb-0">By Camp</h6>
                </div>
                <small className="text-muted d-block mb-2"><Dot c="#e74c3c" />Occupied <Dot c="#e69a16" />Vacant</small>
                {byCamp.length
                  ? <ReactApexChart options={campChart.options} series={campChart.series} type="bar" height={320} />
                  : <p className="text-muted mb-0">No data</p>}
              </CardBody>
            </Card>
          </Col>

          {/* Workforce by Camp Group — headcount vs beds (click a bar → that group's workforce) */}
          <Col lg={6}>
            <Card className="h-100">
              <CardBody>
                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap">
                  <h6 className="fw-semibold mb-0">Workforce by Camp Group</h6>
                  <span className="text-muted small">{g ? `${g.housed} of ${g.total_employees} housed` : ""}</span>
                </div>
                <small className="text-muted d-block mb-2"><Dot c="#2f57c4" />Employees <Dot c="#1a9d5f" />Bed Capacity</small>
                {byGroup.length
                  ? <ReactApexChart options={groupChart.options} series={groupChart.series} type="bar" height={320} />
                  : <p className="text-muted mb-0">No data</p>}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Not in camp — workforce away/unhoused by status, as clickable cards */}
        {g?.not_in_camp && Object.keys(g.not_in_camp).length > 0 && (
          <>
            <h6 className="fw-semibold mt-3 mb-2">Not in Camp</h6>
            <Row className="g-2">
              {Object.entries(g.not_in_camp).map(([k, v]) => (
                <Col key={k}>
                  <Card className="h-100 mb-0" role="button" style={{ cursor: "pointer" }}
                    onClick={() => navigate(`/hrms-workforce?camp=${encodeURIComponent(k)}`)}>
                    <CardBody className="py-2 px-3 d-flex justify-content-between align-items-center">
                      <span className="text-muted text-uppercase" style={{ fontSize: 11, letterSpacing: ".02em", whiteSpace: "nowrap" }}>{k.charAt(0) + k.slice(1).toLowerCase()}</span>
                      <span className="fw-bold ms-2" style={{ fontSize: 18, color: "#d97706" }}>{v}</span>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>
          </>
        )}

        {/* Finance reports — Superadmin / Accounts Manager only (subledger-based) */}
        {canSeeReports && (
          <Card className="mt-4 mb-0">
            <CardBody>
              <style>{`
                /* tighter cell padding buys the amount columns the width they need */
                .camp-report-table thead th, .camp-report-table tbody td { padding: 10px 8px !important; }
                /* Headings may wrap onto two or three lines (aligned to the bottom) so their
                   columns stay narrow. Slightly smaller and never broken mid-word, so a single
                   long word like COMMISSION / CAPACITY stays whole instead of "COMMISSIO N". */
                .camp-report-table thead th {
                  white-space: normal !important; vertical-align: bottom; line-height: 1.2;
                  font-size: 10.5px; letter-spacing: 0; word-break: keep-all; overflow-wrap: normal; hyphens: none;
                }
              `}</style>
              <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <h6 className="fw-semibold mb-0">Camp Cost Reports</h6>
                <div className="d-flex align-items-center gap-2">
                  {(!!(rent?.rows || []).length || !!(cost?.months || []).length) && (
                    <>
                      <Button size="sm" color="success" outline onClick={exportRentXls} title="Export to Excel (all reports)">
                        <i className="bx bx-file me-1" /> XLS
                      </Button>
                      <Button size="sm" color="danger" outline onClick={exportRentPdf} title="Export to PDF (all reports)">
                        <i className="bx bxs-file-pdf me-1" /> PDF
                      </Button>
                    </>
                  )}
                  <span className="text-muted small">Year</span>
                  <Input type="select" bsSize="sm" style={{ width: 100 }} value={year} onChange={e => setYear(Number(e.target.value))}>
                    {[thisYear + 1, thisYear, thisYear - 1, thisYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
                  </Input>
                </div>
              </div>

              {/* Rent Details — the per-cheque register, always shown above the analysis tabs */}
              <h6 className="fw-semibold mb-2">Rent Details</h6>
              <div style={{ overflowX: "auto" }} className="mb-4">
                <Table responsive borderless className="align-middle tnt-table camp-report-table mb-0" style={{ fontSize: 13, minWidth: 1080 }}>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 175 }}>Name</th>
                      <th style={{ width: 135 }}>Cheque No / Date</th>
                      <th className="text-end text-nowrap" style={{ width: 100 }}>Amount</th>
                      <th className="text-end text-nowrap" style={{ width: 90 }}>Camp Rent</th>
                      <th className="text-end text-nowrap" style={{ width: 96 }}>Commission</th>
                      <th className="text-end text-nowrap" style={{ width: 84 }}>Security</th>
                      <th className="text-end text-nowrap" style={{ width: 94 }}>Net Rent</th>
                      <th style={{ width: 120 }}>Location</th>
                      <th className="text-end" style={{ width: 54 }}>Rooms</th>
                      <th style={{ width: 120 }}>Period</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const items = buildChequeItems(rent?.rows)
                      const total = items.reduce((a, it) => a + (Number(it.amount) || 0), 0)
                      const vsum = k => (rent?.rows || []).reduce((a, r) => a + (Number(r[k]) || 0), 0)
                      return (
                        <>
                          {items.map((it, i) => {
                            const r = it.r
                            return (
                              <tr key={i} style={it.first && i > 0 ? { borderTop: "1px solid #eef0f4" } : undefined}>
                                <td className="fw-500">
                                  {it.first ? (
                                    <>
                                      {r.supplier || <span className="text-muted">—</span>}
                                      <small className="text-muted d-block">
                                        {r.ref || ""}{r.ref && r.date ? " · " : ""}{fmtDate(r.date)}
                                      </small>
                                    </>
                                  ) : null}
                                </td>
                                {/* cheque no + date on one line, payment status (coloured) beneath */}
                                <td>
                                  {it.cheque_no && (
                                    <span className="text-nowrap d-block">
                                      {it.cheque_no}
                                      <small className="text-muted ms-1">{fmtDate(it.cheque_date)}</small>
                                    </span>
                                  )}
                                  <span className="fw-500" style={{ color: statusColor(it.status) }}>{it.status}</span>
                                </td>
                                <td className="text-end text-nowrap fw-500">{money(it.amount)}</td>
                                {/* voucher-level breakdown — printed once, on the voucher's first cheque row */}
                                <td className="text-end text-nowrap">{it.first ? money(r.camp_rent) : ""}</td>
                                <td className="text-end text-nowrap">{it.first ? money(r.commission) : ""}</td>
                                <td className="text-end text-nowrap">{it.first ? money(r.security_cheque) : ""}</td>
                                <td className="text-end text-nowrap fw-500">{it.first ? money(r.net_rent) : ""}</td>
                                <td>{it.first ? r.location : ""}</td>
                                <td className="text-end">{it.first ? (r.room_count || "—") : ""}</td>
                                <td className="text-nowrap">
                                  {it.first && r.period_from ? (
                                    <>
                                      {fmtDate(r.period_from)}{r.period_to ? " → " + fmtDate(r.period_to) : ""}
                                      <small className="text-muted d-block">
                                        {r.period === "Yearly"
                                          ? `Yearly (${Math.max(1, Math.round((r.months || 12) / 12))} yr)`
                                          : `Monthly (${r.months || 1} mo)`}
                                      </small>
                                    </>
                                  ) : ""}
                                </td>
                              </tr>
                            )
                          })}
                          {!items.length && <tr><td colSpan="10" className="text-center text-muted">No rent postings for {year}.</td></tr>}
                          {!!items.length && (
                            <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                              <td className="fw-600" colSpan="2">Total ({items.length} line{items.length === 1 ? "" : "s"})</td>
                              <td className="text-end text-nowrap fw-600">{money(total)}</td>
                              <td className="text-end text-nowrap fw-600">{money(vsum("camp_rent"))}</td>
                              <td className="text-end text-nowrap fw-600">{money(vsum("commission"))}</td>
                              <td className="text-end text-nowrap fw-600">{money(vsum("security_cheque"))}</td>
                              <td className="text-end text-nowrap fw-600">{money(rent?.total)}</td>
                              <td colSpan="3"></td>
                            </tr>
                          )}
                        </>
                      )
                    })()}
                  </tbody>
                </Table>
              </div>

              <Nav tabs className="mb-3">
                <NavItem><NavLink role="button" active={reportTab === "rent"} onClick={() => setReportTab("rent")}>Rent Analysis</NavLink></NavItem>
                <NavItem><NavLink role="button" active={reportTab === "cost"} onClick={() => setReportTab("cost")}>Cost Analysis</NavLink></NavItem>
              </Nav>

              {reportTab === "cost" ? (
                <>
                  {(() => {
                    const eh = cost?.expense_heads || []
                    const rh = cost?.recovery_heads || []
                    const hasInc = !!cost?.has_incentive
                    const recCols = rh.length + (hasInc ? 1 : 0)
                    const t = cost?.totals || {}
                    const av = cost?.averages || {}
                    const perHr = v => (v == null ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 }))
                    return (
                      <>
                      <div style={{ overflowX: "auto" }}>
                        <Table responsive borderless className="align-middle tnt-table camp-report-table mb-0" style={{ fontSize: 13, minWidth: 1100 }}>
                          <thead>
                            <tr>
                              <th rowSpan={2} style={{ width: 70 }}>Month</th>
                              {!!eh.length && <th colSpan={eh.length} className="text-center" style={{ background: "#eef2ff" }}>Expenses</th>}
                              <th rowSpan={2} className="text-end">Total Expenses</th>
                              {recCols > 0 && <th colSpan={recCols} className="text-center" style={{ background: "#fff5eb" }}>Recovered</th>}
                              <th rowSpan={2} className="text-end">Total Recovered</th>
                              <th rowSpan={2} className="text-end">Net Cost</th>
                              <th rowSpan={2} className="text-end">Invoiced Hours</th>
                              <th rowSpan={2} className="text-end">Cost / Hour</th>
                              <th rowSpan={2} className="text-end">Employee Count</th>
                              <th rowSpan={2} className="text-end">Per Head / Month</th>
                            </tr>
                            <tr>
                              {eh.map(h => <th key={h} className="text-end" style={{ background: "#f5f7ff" }}>{h}</th>)}
                              {rh.map(h => <th key={h} className="text-end" style={{ background: "#fffaf3" }}>{h}</th>)}
                              {hasInc && <th className="text-end" style={{ background: "#fffaf3" }}>Incentive (−)</th>}
                            </tr>
                          </thead>
                          <tbody>
                            {(cost?.months || []).map((m, i) => (
                              <tr key={m.month}>
                                <td>{m.label}</td>
                                {eh.map(h => <td key={h} className="text-end">{money(m.expenses?.[h])}</td>)}
                                <td className="text-end fw-500">{money(m.total_expenses)}</td>
                                {rh.map(h => <td key={h} className="text-end">{money(m.recovered?.[h])}</td>)}
                                {hasInc && <td className="text-end">{money(m.incentive)}</td>}
                                <td className="text-end fw-500">{money(m.total_recovered)}</td>
                                <td className="text-end fw-600">{money(m.net_cost)}</td>
                                <td className="text-end text-muted">{m.invoiced_hours != null ? m.invoiced_hours : "—"}</td>
                                <td className="text-end text-muted">{m.cost_per_hour != null ? money(m.cost_per_hour) : "—"}</td>
                                <td className="text-end text-muted">{m.employee_count != null ? m.employee_count : "—"}</td>
                                <td className="text-end text-muted">{m.per_head != null ? money(m.per_head) : "—"}</td>
                              </tr>
                            ))}
                            <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                              <td className="fw-600" colSpan={1}>Total</td>
                              {eh.map(h => <td key={h} className="text-end fw-600">{money(t.expenses?.[h])}</td>)}
                              <td className="text-end fw-600">{money(t.total_expenses)}</td>
                              {rh.map(h => <td key={h} className="text-end fw-600">{money(t.recovered?.[h])}</td>)}
                              {hasInc && <td className="text-end fw-600">{money(t.incentive)}</td>}
                              <td className="text-end fw-600">{money(t.total_recovered)}</td>
                              <td className="text-end fw-600">{money(t.net_cost)}</td>
                              <td className="text-end">—</td>
                              <td className="text-end">—</td>
                              <td className="text-end">—</td>
                              <td className="text-end">—</td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>

                      {/* Summary cards */}
                      <Row className="g-3 mt-3 mb-1">
                        {[
                          { label: "Average Expenses / Month", val: money(av.avg_expenses), sub: `Net Cost ÷ ${av.months_counted || 0} mo` },
                          { label: "Average Working Hours / Month", val: av.avg_working_hours ? Number(av.avg_working_hours).toLocaleString() : "—", sub: "from HRMS attendance" },
                          { label: "Average Cost / Month", val: av.avg_cost != null ? money(av.avg_cost) : "—", sub: "Net Cost ÷ Working Hours" },
                        ].map((c, i) => (
                          <Col md={4} key={i}>
                            <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "14px 16px", background: "#fff", height: "100%" }}>
                              <div className="text-muted small">{c.label}</div>
                              <div className="fw-600" style={{ fontSize: 20, lineHeight: 1.4 }}>{c.val}</div>
                              <div className="text-muted" style={{ fontSize: 11 }}>{c.sub}</div>
                            </div>
                          </Col>
                        ))}
                      </Row>

                      {/* 2nd report — Cost per Hour (per-head amount ÷ working hours) */}
                      <h6 className="fw-semibold mt-4 mb-2">Cost per Hour</h6>
                      <div style={{ overflowX: "auto" }}>
                        <Table responsive borderless className="align-middle tnt-table camp-report-table mb-0" style={{ fontSize: 13, minWidth: 1100 }}>
                          <thead>
                            <tr>
                              <th rowSpan={2} style={{ width: 70 }}>Month</th>
                              <th rowSpan={2} className="text-end" style={{ width: 90 }}>Working Hours</th>
                              {eh.map(h => <th key={h} colSpan={2} className="text-center" style={{ background: "#eef2ff" }}>{h}</th>)}
                              <th rowSpan={2} className="text-end">Total Expenses</th>
                              <th rowSpan={2} className="text-end">Cost / Hour</th>
                            </tr>
                            <tr>
                              {eh.flatMap(h => [
                                <th key={h + "a"} className="text-end" style={{ background: "#f5f7ff" }}>Amount</th>,
                                <th key={h + "r"} className="text-end" style={{ background: "#f5f7ff" }}>/ Hr</th>,
                              ])}
                            </tr>
                          </thead>
                          <tbody>
                            {(cost?.months || []).map((m, i) => (
                              <tr key={m.month}>
                                <td>{m.label}</td>
                                <td className="text-end text-muted">{m.working_hours != null ? Number(m.working_hours).toLocaleString() : "—"}</td>
                                {eh.flatMap(h => [
                                  <td key={h + "a"} className="text-end">{money(m.expenses?.[h])}</td>,
                                  <td key={h + "r"} className="text-end text-muted">{perHr(m.expense_cost_per_hour?.[h])}</td>,
                                ])}
                                <td className="text-end fw-500">{money(m.total_expenses)}</td>
                                <td className="text-end fw-600">{perHr(m.total_cost_per_hour)}</td>
                              </tr>
                            ))}
                            <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                              <td className="fw-600" colSpan={1}>Total</td>
                              <td className="text-end fw-600">{t.working_hours ? Number(t.working_hours).toLocaleString() : "—"}</td>
                              {eh.flatMap(h => [
                                <td key={h + "a"} className="text-end fw-600">{money(t.expenses?.[h])}</td>,
                                <td key={h + "r"} className="text-end">—</td>,
                              ])}
                              <td className="text-end fw-600">{money(t.total_expenses)}</td>
                              <td className="text-end">—</td>
                            </tr>
                          </tbody>
                        </Table>
                      </div>
                      </>
                    )
                  })()}
                </>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  {/* Main Abstract — per-camp summary of the invoice lines above */}
                  {!!(rent?.abstract || []).length && (
                    <>
                      <h6 className="fw-semibold mt-4 mb-2">Main Abstract — Occupied Capacity</h6>
                      <Table responsive borderless className="align-middle tnt-table camp-report-table mb-0" style={{ fontSize: 13, tableLayout: "fixed", minWidth: 1130 }}>
                        <thead>
                          <tr>
                            <th style={{ width: 175 }}>Camp</th>
                            <th className="text-end text-nowrap" style={{ width: 95 }}>Camp Rent</th>
                            <th className="text-end text-nowrap" style={{ width: 98 }}>Commission</th>
                            <th className="text-end text-nowrap" style={{ width: 92 }}>Security</th>
                            <th className="text-end text-nowrap" style={{ width: 98 }}>Net Rent</th>
                            <th className="text-end text-nowrap" style={{ width: 92 }}>Cleared</th>
                            <th className="text-end text-nowrap" style={{ width: 92 }}>Issued</th>
                            <th className="text-end" style={{ width: 95 }} title="Amount with no cheque written against it yet. Cleared + Issued + Not Issued = Net Rent.">Not Issued</th>
                            <th className="text-end" style={{ width: 80 }}>Rooms / Capacity</th>
                            <th className="text-end" style={{ width: 72 }}>Rent / Emp</th>
                            <th className="text-end" style={{ width: 74 }}>+ Commission / Emp</th>
                            <th className="text-end" style={{ width: 74 }}>+ Comm + VAT / Emp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(rent?.abstract || []).map((a, i) => (
                            <tr key={i}>
                              <td className="fw-500">
                                {a.camp}
                                {(a.duration || a.period_from) && (
                                  <small className="text-muted d-block">
                                    {a.duration}
                                    {a.period_from ? ` · ${fmtDate(a.period_from)}${a.period_to ? " → " + fmtDate(a.period_to) : ""}` : ""}
                                  </small>
                                )}
                              </td>
                              <td className="text-end text-nowrap">{money(a.camp_rent)}</td>
                              <td className="text-end text-nowrap">{money(a.commission)}</td>
                              <td className="text-end text-nowrap">{money(a.security_cheque)}</td>
                              <td className="text-end text-nowrap fw-500">{money(a.net_rent)}</td>
                              <td className="text-end text-nowrap">{money(a.cleared)}</td>
                              <td className="text-end text-nowrap">{money(a.issued)}</td>
                              <td className="text-end text-nowrap">{money(a.need_to_give)}</td>
                              <td className="text-end text-nowrap">{a.room_count || "—"} / {a.capacity || "—"}</td>
                              <td className="text-end">{a.rent_per_employee != null ? money(a.rent_per_employee) : <span className="text-muted">—</span>}</td>
                              <td className="text-end">{a.rent_comm_per_employee != null ? money(a.rent_comm_per_employee) : <span className="text-muted">—</span>}</td>
                              <td className="text-end">{a.rent_vat_per_employee != null ? money(a.rent_vat_per_employee) : <span className="text-muted">—</span>}</td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                            <td className="fw-600">Total</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.camp_rent)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.commission)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.security_cheque)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.net_rent)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.cleared)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.issued)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.need_to_give)}</td>
                            <td className="text-end fw-600 text-nowrap">{rent?.abstract_total?.room_count || "—"} / {rent?.abstract_total?.capacity || "—"}</td>
                            <td className="text-end fw-600">{rent?.abstract_total?.rent_per_employee != null ? money(rent.abstract_total.rent_per_employee) : "—"}</td>
                            <td className="text-end fw-600">{rent?.abstract_total?.rent_comm_per_employee != null ? money(rent.abstract_total.rent_comm_per_employee) : "—"}</td>
                            <td className="text-end fw-600">{rent?.abstract_total?.rent_vat_per_employee != null ? money(rent.abstract_total.rent_vat_per_employee) : "—"}</td>
                          </tr>
                        </tbody>
                      </Table>

                      {/* 3rd report — same abstract, but Rent / Employee against ACTUAL capacity
                          (Max Occupancy, e.g. a 10-bed room actually housing 12) */}
                      <h6 className="fw-semibold mt-4 mb-2">Main Abstract — Actual Capacity</h6>
                      <Table responsive borderless className="align-middle tnt-table camp-report-table mb-0" style={{ fontSize: 13, tableLayout: "fixed", minWidth: 1134 }}>
                        <thead>
                          <tr>
                            <th style={{ width: 175 }}>Camp</th>
                            <th className="text-end text-nowrap" style={{ width: 95 }}>Camp Rent</th>
                            <th className="text-end text-nowrap" style={{ width: 98 }}>Commission</th>
                            <th className="text-end text-nowrap" style={{ width: 92 }}>Security</th>
                            <th className="text-end text-nowrap" style={{ width: 98 }}>Net Rent</th>
                            <th className="text-end text-nowrap" style={{ width: 92 }}>Cleared</th>
                            <th className="text-end text-nowrap" style={{ width: 92 }}>Issued</th>
                            <th className="text-end" style={{ width: 95 }} title="Amount with no cheque written against it yet. Cleared + Issued + Not Issued = Net Rent.">Not Issued</th>
                            <th className="text-end" style={{ width: 84 }}>Rooms / Actual Capacity</th>
                            <th className="text-end" style={{ width: 72 }}>Rent / Emp</th>
                            <th className="text-end" style={{ width: 74 }}>+ Commission / Emp</th>
                            <th className="text-end" style={{ width: 74 }}>+ Comm + VAT / Emp</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(rent?.abstract || []).map((a, i) => (
                            <tr key={i}>
                              <td className="fw-500">
                                {a.camp}
                                {(a.duration || a.period_from) && (
                                  <small className="text-muted d-block">
                                    {a.duration}
                                    {a.period_from ? ` · ${fmtDate(a.period_from)}${a.period_to ? " → " + fmtDate(a.period_to) : ""}` : ""}
                                  </small>
                                )}
                              </td>
                              <td className="text-end text-nowrap">{money(a.camp_rent)}</td>
                              <td className="text-end text-nowrap">{money(a.commission)}</td>
                              <td className="text-end text-nowrap">{money(a.security_cheque)}</td>
                              <td className="text-end text-nowrap fw-500">{money(a.net_rent)}</td>
                              <td className="text-end text-nowrap">{money(a.cleared)}</td>
                              <td className="text-end text-nowrap">{money(a.issued)}</td>
                              <td className="text-end text-nowrap">{money(a.need_to_give)}</td>
                              <td className="text-end text-nowrap">{a.room_count || "—"} / {a.actual_capacity || "—"}</td>
                              <td className="text-end">{a.rent_per_actual != null ? money(a.rent_per_actual) : <span className="text-muted">—</span>}</td>
                              <td className="text-end">{a.rent_comm_per_actual != null ? money(a.rent_comm_per_actual) : <span className="text-muted">—</span>}</td>
                              <td className="text-end">{a.rent_vat_per_actual != null ? money(a.rent_vat_per_actual) : <span className="text-muted">—</span>}</td>
                            </tr>
                          ))}
                          <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                            <td className="fw-600">Total</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.camp_rent)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.commission)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.security_cheque)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.net_rent)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.cleared)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.issued)}</td>
                            <td className="text-end text-nowrap fw-600">{money(rent?.abstract_total?.need_to_give)}</td>
                            <td className="text-end fw-600 text-nowrap">{rent?.abstract_total?.room_count || "—"} / {rent?.abstract_total?.actual_capacity || "—"}</td>
                            <td className="text-end fw-600">{rent?.abstract_total?.rent_per_actual != null ? money(rent.abstract_total.rent_per_actual) : "—"}</td>
                            <td className="text-end fw-600">{rent?.abstract_total?.rent_comm_per_actual != null ? money(rent.abstract_total.rent_comm_per_actual) : "—"}</td>
                            <td className="text-end fw-600">{rent?.abstract_total?.rent_vat_per_actual != null ? money(rent.abstract_total.rent_vat_per_actual) : "—"}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        )}
      </Container>
    </div>
  )
}

export default CampDashboard
